import { useFormContext, useWatch } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import { Form } from "@/components/ui/form";
import { ServicePreviewCard } from "@/domains/menu/services/components/cards/service-preview-card";
import { BackButton, BaseNavigationBar } from "@/lib/components/navigation";
import { FormActionButtons, NavigationGuardProvider } from "@/lib/components/navigation-guard";
import { PageContent } from "@/lib/components/page-content";
import { ServiceInformationForm, ServicePricingForm } from "../components";
import { useServiceEdit } from "../hooks";
import type { ServiceEditFormData } from "../validation/service-edit-schema";

function EditServicePreview() {
  const { control } = useFormContext<ServiceEditFormData>();
  const values = useWatch({ control });
  const basicInfo = values.basicInfo;
  const pricing = values.pricing;
  return (
    <ServicePreviewCard
      name={basicInfo?.name || ""}
      description={basicInfo?.description}
      duration={basicInfo?.duration || 0}
      pricingMode={pricing?.pricingMode ?? "SINGLE_PRICE"}
      basePrice={(pricing?.basePrice ?? 0) / 100}
      optionGroups={values.optionGroups
        ?.filter(
          (g): g is NonNullable<typeof g> & { selectionType: "SINGLE" | "MULTIPLE" } =>
            !!g && (g.selectionType === "SINGLE" || g.selectionType === "MULTIPLE")
        )
        .map((g) => ({
          name: g.name ?? "",
          selectionType: g.selectionType,
          options: (g.options ?? []).map((o) => ({
            optionName: o?.optionName ?? "",
            priceModifier: (o?.priceModifier ?? 0) / 100,
          })),
        }))}
      displayMode={basicInfo?.storefrontDisplayMode ?? null}
    />
  );
}
// Removed individual dialog imports - using bulk form-based updates instead
// import { OptionGroupDialog, OptionDialog, DeleteConfirmDialog } from "@/domains/services/components/dialogs";
// import { useServiceEditDialogStore } from "../store/service-edit-store";

export function ServiceEditPage() {
  const { t } = useTranslation("services");
  const { serviceId } = useParams<{ serviceId: string }>();
  const navigate = useNavigate();

  // Hook for data fetching and form management
  const { service, categories, form, loading, saving, save, discard } = useServiceEdit({
    serviceId: serviceId || "",
  });

  if (!form) return null;

  return (
    <NavigationGuardProvider
      hasUnsavedChanges={form.formState.isDirty}
      onSave={save}
      onDiscard={discard}
      saving={saving}
      description={t("serviceEditPage.unsavedChangesDescription")}
    >
      <BaseNavigationBar
        title={t("serviceEditPage.title")}
        subtitle={service?.name || t("serviceEditPage.loadingSubtitle")}
        backButton={<BackButton key="back-button" onBack={() => navigate("/services")} />}
        trailingElements={[
          <FormActionButtons
            key="form-actions"
            isDirty={form.formState.isDirty}
            onSave={save}
            onDiscard={discard}
            saving={saving}
          />,
        ]}
      />

      <PageContent>
        <Form {...form}>
          <div className="space-y-6">
            <ServiceInformationForm categories={categories} loading={loading} saving={saving} />
            <ServicePricingForm loading={loading} saving={saving} />
            <EditServicePreview />
          </div>
        </Form>

        {/* Removed individual dialogs - using bulk form-based updates through upsertServiceWithMatrixPricing */}
        {/* All option editing is now handled through the form state and the save button triggers the bulk mutation */}
      </PageContent>
    </NavigationGuardProvider>
  );
}
